import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import { Box, Button, Grid, Stack, TextField, createTheme, useMediaQuery } from '@mui/material';
import './style.css';
import LeadStore from '../../store/LeadStore';
import { isEmpty } from '../../utils/helper';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditClientInfo = observer(({ leadId, clientInfo, handleEditClientInfo }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    id: clientInfo?.clientBasicInfoId ?? null,
    leadId,
    clientName: clientInfo?.clientName,
    primaryContactNumber: clientInfo?.primaryContactNumber,
    secondaryContactNumber: clientInfo?.secondaryContactNumber,
    clientPointOfContact: clientInfo?.clientPointOfContact,
    clientResidingAddress1: clientInfo?.clientResidingAddress1,
    clientResidingAddress2: clientInfo?.clientResidingAddress2,
    createdBy: clientInfo?.CreatedBy,
    createdDate: clientInfo?.createdDate,
    updatedBy: clientInfo?.updatedBy,
    updatedDate: clientInfo?.updatedDate,
  });

  const [errors, setErrors] = useState({
    clientName: false,
    primaryContactNumber: false,
    secondaryContactNumber: false,
    clientPointOfContact: false,
    clientResidingAddress1: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    clientName: '',
    primaryContactNumber: '',
    secondaryContactNumber: '',
    clientPointOfContact: '',
    clientResidingAddress1: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Perform validation
    if (name === 'secondaryContactNumber') {
      setErrors({ ...errors, [name]: isContactNumberError(value, name) });
    } else if (name === 'primaryContactNumber') {
      setErrors({ ...errors, [name]: isNumberError(value, name) });
    } else {
      setErrors({ ...errors, [name]: isEmpty(value) });
    }
  };

  const handleUpdateClientInfoSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      clientName: isEmpty(formData.clientName),
      primaryContactNumber: isNumberError(formData.primaryContactNumber, 'primaryContactNumber'),
      secondaryContactNumber: isContactNumberError(formData.secondaryContactNumber),
      clientPointOfContact: isEmpty(formData.clientPointOfContact),
      clientResidingAddress1: isEmpty(formData.clientResidingAddress1),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      isEmpty(formData.id) ? LeadStore.createClientInfo(payload) : LeadStore.updateClientInfo(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateClientInfoStatus && handleEditClientInfo();
  }, [LeadStore.updateClientInfoStatus]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="client-info-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateClientInfoSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Client Name"
                    required
                    id="client-name"
                    size="small"
                    variant="standard"
                    name="clientName"
                    value={formData.clientName ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.clientName)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Client Point of Contact"
                    required
                    id="clientPointOfContact"
                    size="small"
                    variant="standard"
                    name="clientPointOfContact"
                    value={formData.clientPointOfContact ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.clientPointOfContact)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Client Residing Address1"
                    required
                    id="clientResidingAddress1"
                    size="small"
                    variant="standard"
                    name="clientResidingAddress1"
                    value={formData.clientResidingAddress1 ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.clientResidingAddress1)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Contact Number (Primary)"
                    required
                    id="primaryContactNumber"
                    size="small"
                    variant="standard"
                    name="primaryContactNumber"
                    sx={{ fontSize: '12px' }}
                    value={formData.primaryContactNumber ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.primaryContactNumber)}
                    helperText={Boolean(errors.primaryContactNumber) && errorMessages.primaryContactNumber}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Contact Number (Secondary)"
                    id="secondaryContactNumber"
                    size="small"
                    variant="standard"
                    name="secondaryContactNumber"
                    sx={{ fontSize: '12px' }}
                    value={formData.secondaryContactNumber ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.secondaryContactNumber)}
                    helperText={Boolean(errors.secondaryContactNumber) && errorMessages.secondaryContactNumber}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Client Residing Address2"
                    id="clientResidingAddress2"
                    size="small"
                    variant="standard"
                    name="clientResidingAddress2"
                    value={formData.clientResidingAddress2 ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              size="small"
              onClick={() => handleEditClientInfo(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
